const MONOTONIC_FLOW_FIELDS = {
  vol: 'dVol',
  bv: 'dBuy',
  sv: 'dSell',
  gb: 'dGb',
  gs: 'dGs',
};
const MONOTONIC_DIRECTIONAL_DELTA_FIELDS = {
  dbBull: 'dDeltaBuyBull',
  dbBear: 'dDeltaBuyBear',
  dsBull: 'dDeltaSellBull',
  dsBear: 'dDeltaSellBear',
};
const RECOVERY_GAP_MINUTES = 5;
const MINUTE_MS = 60_000;

function num(v) {
  return Number(v) || 0;
}

function finiteNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function derivedDirectionalDeltaCounter(cell = {}, field) {
  const explicit = finiteNum(cell[field]);
  if (explicit != null) return explicit;
  const db = finiteNum(cell.db);
  const ds = finiteNum(cell.ds);
  switch (field) {
    case 'dbBull':
      return db == null ? null : Math.max(0, db);
    case 'dbBear':
      return db == null ? null : Math.max(0, -db);
    case 'dsBull':
      return ds == null ? null : Math.max(0, -ds);
    case 'dsBear':
      return ds == null ? null : Math.max(0, ds);
    default:
      return null;
  }
}

function minuteBucketMs(t) {
  if (t == null || t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return Math.floor(n / MINUTE_MS) * MINUTE_MS;
}

// Per-minute diff of the cumulative counters. Volume/side counters are clamped
// per-field by design: they only grow within a session, so a real feed reset /
// new session drives those fields negative at once and they zero. `dNet` is
// signed and is not clamped, so a reset can emit a large negative dNet with
// every other field at zero. Harmless for callers that read `dBuy`/`dSell`
// (`netVolByLeg`); a lone negative volume field still cannot occur mid-session.
// Returns null when nothing changed so callers can skip empty cells.
function diffCell(curr = {}, prev = {}) {
  const out = {};
  let any = false;
  for (const [src, dst] of Object.entries(MONOTONIC_FLOW_FIELDS)) {
    const dx = Math.max(0, num(curr[src]) - num(prev[src]));
    out[dst] = dx;
    if (dx !== 0) any = true;
  }
  out.dNet = num(curr.nv) - num(prev.nv);
  if (out.dNet !== 0) any = true;
  if (curr.hasLegSplit === true && prev.hasLegSplit === true) {
    out.dCallVol = Math.max(0, num(curr.cvol) - num(prev.cvol));
    out.dPutVol = Math.max(0, num(curr.pvol) - num(prev.pvol));
    const callNet = num(curr.cnv) - num(prev.cnv);
    const putNet = num(curr.pnv) - num(prev.pnv);
    out.dCallNet = Math.max(-out.dCallVol, Math.min(out.dCallVol, callNet));
    out.dPutNet = Math.max(-out.dPutVol, Math.min(out.dPutVol, putNet));
    out.hasLegSplit = true;
    if (out.dCallNet !== 0 || out.dPutNet !== 0) any = true;
  }
  if (out.dVol > 0 && out.dBuy + out.dSell <= 0) {
    const net = Math.max(-out.dVol, Math.min(out.dVol, out.dNet));
    out.dBuy = (out.dVol + net) / 2;
    out.dSell = (out.dVol - net) / 2;
  }
  const currDeltaBuy = finiteNum(curr.db);
  const prevDeltaBuy = finiteNum(prev.db);
  const currDeltaSell = finiteNum(curr.ds);
  const prevDeltaSell = finiteNum(prev.ds);
  const hasDeltaSides = currDeltaBuy != null || prevDeltaBuy != null || currDeltaSell != null || prevDeltaSell != null;
  const currDelta = finiteNum(curr.d);
  const prevDelta = finiteNum(prev.d);
  if ((out.dVol > 0 || hasDeltaSides) && (currDelta != null || prevDelta != null)) {
    out.dDeltaNet = (currDelta ?? 0) - (prevDelta ?? 0);
    if (out.dDeltaNet !== 0) any = true;
  }

  if (hasDeltaSides) {
    out.dDeltaBuy = (currDeltaBuy ?? 0) - (prevDeltaBuy ?? 0);
    out.dDeltaSell = (currDeltaSell ?? 0) - (prevDeltaSell ?? 0);
    if (out.dDeltaBuy !== 0 || out.dDeltaSell !== 0) any = true;
  }
  for (const [src, dst] of Object.entries(MONOTONIC_DIRECTIONAL_DELTA_FIELDS)) {
    const currDirectional = derivedDirectionalDeltaCounter(curr, src);
    const prevDirectional = derivedDirectionalDeltaCounter(prev, src);
    const dx = Math.max(0, (currDirectional ?? 0) - (prevDirectional ?? 0));
    out[dst] = dx;
    if (dx !== 0) any = true;
  }
  return any ? out : null;
}

export function minuteFlows(snaps = []) {
  const out = [];
  for (let j = 1; j < snaps.length; j += 1) {
    const prevBucket = minuteBucketMs(snaps[j - 1]?.t);
    const currBucket = minuteBucketMs(snaps[j]?.t);
    if (!Number.isFinite(prevBucket) || !Number.isFinite(currBucket)) continue;
    // The point-in-time sample at M closes the counter interval for M-1.
    const flowMinute = currBucket - MINUTE_MS;
    const gapMinutes = (
      currBucket > prevBucket
    )
      ? Math.max(0, Math.round((currBucket - prevBucket) / MINUTE_MS) - 1)
      : 0;
    const recoveryGap = gapMinutes >= RECOVERY_GAP_MINUTES;
    const prevByStrike = snaps[j - 1]?.byStrike || {};
    const currByStrike = snaps[j]?.byStrike || {};
    // Only diff strikes present in both snapshots; otherwise a window/filter change
    // turns a cumulative session total into a fake one-minute print. Live init has
    // zero-counter rows, so this only drops a first print across a source boundary.
    const strikes = Object.keys(currByStrike)
      .filter((strike) => Object.prototype.hasOwnProperty.call(prevByStrike, strike));

    // One real minute is intentionally blanked here to avoid rendering the
    // cumulative baseline jump between hydrated history and live snapshots.
    if (snaps[j - 1]?.hydrationBoundaryTail || snaps[j]?.hydrationSeam) {
      out.push({
        t: flowMinute,
        spot: snaps[j - 1]?.spot,
        recoveryGap,
        gapMinutes,
        hydrationSeam: Boolean(snaps[j]?.hydrationSeam),
        hydrationBoundaryTail: Boolean(snaps[j - 1]?.hydrationBoundaryTail),
        byStrike: {},
      });
      continue;
    }

    const byStrike = {};
    for (const strike of strikes) {
      // Skip quiet/vanished strikes - a minute with no flow contributes no cell
      // (consumers default a missing cell to zeros).
      const cell = diffCell(currByStrike[strike], prevByStrike[strike]);
      if (cell) byStrike[strike] = cell;
    }

    out.push({
      t: flowMinute,
      spot: snaps[j - 1]?.spot,
      recoveryGap,
      gapMinutes,
      byStrike,
    });
  }
  return out;
}

export function gammaSplit(cell = {}) {
  const pos = Math.max(0, num(cell.dGs));
  const neg = Math.max(0, num(cell.dGb));
  const total = pos + neg;
  return {
    pos,
    neg,
    share: total > 0 ? pos / total : 0.5,
  };
}

// Snap a price to the nearest strike on the fixed step grid (SPX strikes are 5 apart).
export function snapStrike(price, step = 5) {
  return Number.isFinite(price) ? Math.round(price / step) * step : null;
}

// Net per-strike value for a heatmap layer, read from a chain snapshot cell.
export function cellValueAt(snap, strike, layer = 'gamma') {
  const cell = snap?.byStrike?.[strike];
  if (!cell) return 0;
  if (layer === 'delta') return Number(cell.d) || 0;
  if (layer === 'volume') {
    return cell.hasLegSplit === true
      ? (Number(cell.cnv) || 0) - (Number(cell.pnv) || 0)
      : (Number(cell.nv) || 0);
  }
  return Number(cell.g) || 0;
}

export function netVolByLeg(flowSnap = {}, strikes = []) {
  const byStrike = flowSnap.byStrike || {};
  let total = 0;
  let callNet = 0;
  let putNet = 0;
  let adjusted = 0;
  let hasFlow = false;
  let hasLegSplit = true;
  for (const strike of strikes) {
    const cell = byStrike[strike] || {};
    const combinedNet = num(cell.dBuy) - num(cell.dSell);
    total += combinedNet;
    const cellHasFlow = num(cell.dVol) !== 0 || num(cell.dNet) !== 0 || num(cell.dBuy) !== 0 || num(cell.dSell) !== 0;
    if (!cellHasFlow) continue;
    hasFlow = true;
    if (cell.hasLegSplit === true) {
      callNet += num(cell.dCallNet);
      putNet += num(cell.dPutNet);
      adjusted += num(cell.dCallNet) - num(cell.dPutNet);
    } else {
      hasLegSplit = false;
      adjusted += combinedNet;
    }
  }
  return { total, callNet, putNet, adjusted, hasLegSplit: hasFlow && hasLegSplit };
}

export function netVol(flowSnap = {}, strikes = [], leg = 'combined') {
  const totals = netVolByLeg(flowSnap, strikes);
  if (leg === 'call') return totals.hasLegSplit ? totals.callNet : null;
  if (leg === 'put') return totals.hasLegSplit ? totals.putNet : null;
  return totals.total;
}

// Strike scope for a bubble's flow aggregation; matches OptionChainTable's spot +/-80 view.
export const STRIKE_WINDOW_PTS = 80;

export function clampBubbleCenter(center, radius, top, bottom) {
  if (![center, top, bottom].every(Number.isFinite) || bottom < top) return center;
  const safeRadius = Number.isFinite(radius) ? Math.max(0, radius) : 0;
  if (safeRadius * 2 > bottom - top) return (top + bottom) / 2;
  return Math.max(top + safeRadius, Math.min(bottom - safeRadius, center));
}

// Drop byStrike cells far outside the near-the-money window used everywhere else
// in this app (bubble aggregation, server-side SQL projection). Guards against
// upstream contamination - e.g. another expiration's contract sharing a numeric
// strike with today's 0-DTE chain - leaking into the heatmap regardless of source.
// No-op when spot is unknown, since there is nothing to window against.
export function windowedSnap(snap, windowPts = STRIKE_WINDOW_PTS) {
  const spot = Number(snap?.spot);
  if (!Number.isFinite(spot) || !Number.isFinite(windowPts)) return snap;
  const byStrike = {};
  let changed = false;
  for (const [strikeKey, cell] of Object.entries(snap?.byStrike || {})) {
    const strike = Number(strikeKey);
    if (Number.isFinite(strike) && Math.abs(strike - spot) <= windowPts) {
      byStrike[strikeKey] = cell;
    } else {
      changed = true;
    }
  }
  return changed ? { ...snap, byStrike } : snap;
}

// Sum a flow snapshot's per-strike cells into one bubble aggregate. When spot is finite,
// only strikes within windowPts of spot are counted (so a bubble's net/bought/sold cover
// the same near-the-money scope as the chain). avgStrike is null when no volume traded.
export function aggregateFlowCells(byStrike = {}, spot = null, windowPts = STRIKE_WINDOW_PTS) {
  const windowed = Number.isFinite(spot) && Number.isFinite(windowPts);
  let dVol = 0;
  let dBuy = 0;
  let dSell = 0;
  let dDeltaBuy = 0;
  let dDeltaSell = 0;
  let dDeltaNet = 0;
  let dDeltaBuyBull = 0;
  let dDeltaBuyBear = 0;
  let dDeltaSellBull = 0;
  let dDeltaSellBear = 0;
  let wSum = 0;
  for (const [strikeStr, cell] of Object.entries(byStrike)) {
    const strike = Number(strikeStr);
    if (windowed && Math.abs(strike - spot) > windowPts) continue;
    const vol = num(cell.dVol);
    dVol += vol;
    dBuy += num(cell.dBuy);
    dSell += num(cell.dSell);
    dDeltaBuy += num(cell.dDeltaBuy);
    dDeltaSell += num(cell.dDeltaSell);
    dDeltaNet += num(cell.dDeltaNet);
    dDeltaBuyBull += num(cell.dDeltaBuyBull);
    dDeltaBuyBear += num(cell.dDeltaBuyBear);
    dDeltaSellBull += num(cell.dDeltaSellBull);
    dDeltaSellBear += num(cell.dDeltaSellBear);
    wSum += strike * vol;
  }
  return {
    dVol,
    dBuy,
    dSell,
    dDeltaBuy,
    dDeltaSell,
    dDeltaNet,
    dDeltaBuyBull,
    dDeltaBuyBear,
    dDeltaSellBull,
    dDeltaSellBear,
    avgStrike: dVol > 0 ? wSum / dVol : null,
  };
}

// Delta contribution to net uses the feed convention net = bought - sold.
// A negative sold delta is therefore bullish pressure, while a positive sold
// delta is bearish pressure.
export function deltaContributionTone(side, delta) {
  const value = num(delta);
  const contribution = side === 'sold' ? -value : value;
  if (contribution > 0) return 'bullish';
  if (contribution < 0) return 'bearish';
  return 'neutral';
}

export function netDeltaTone(delta) {
  const value = num(delta);
  if (value > 0) return 'bullish';
  if (value < 0) return 'bearish';
  return 'neutral';
}

export function toneClassName(tone, bullishClass, bearishClass) {
  if (tone === 'bullish') return bullishClass;
  if (tone === 'bearish') return bearishClass;
  return '';
}

// p-th percentile of a numeric array (p in [0,1]).
export function percentile(values, p) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length * p)] ?? 0;
}

export const VOLUME_DOT_MIN_VOLUME = 10000;
export const VOLUME_DOT_MIN_RADIUS = 2.5;
export const VOLUME_DOT_MAX_RADIUS = 13;
export const VOLUME_DOT_RECOVERY_RADIUS = 4.5;
export const VOLUME_DOT_MIN_VISIBLE_WEDGE_DEG = 8;
export const VOLUME_DOT_AMBIGUOUS_STYLE = {
  hatchAngleDeg: 135,
  hatchColor: '226,232,240',
  hatchAlpha: 0.82,
  hatchWidthPx: 1.5,
  hatchGapPx: 6,
  canvasHatchWidthPx: 1.1,
};

// Dot radius for a volume print. Scales by sqrt(dVol/p98).
export function dotRadius(
  dVol,
  p98,
  minR = VOLUME_DOT_MIN_RADIUS,
  maxR = VOLUME_DOT_MAX_RADIUS
) {
  const scaled = Math.sqrt(Math.max(0, dVol) / Math.max(1, p98));
  return Math.min(maxR, minR + (maxR - minR) * scaled);
}

export function displayVolumeDotShare(share) {
  const bounded = Math.max(0, Math.min(1, num(share)));
  const minority = Math.min(bounded, 1 - bounded);
  if (minority === 0) return bounded;
  const minMinorityShare = VOLUME_DOT_MIN_VISIBLE_WEDGE_DEG / 360;
  const displayMinority = Math.max(minority, minMinorityShare);
  return bounded >= 0.5 ? 1 - displayMinority : displayMinority;
}

function directionalSegment(side, bull, bear) {
  const total = bull + bear;
  if (total <= 0) return null;
  const share = bull / total;
  const rawCounterShare = Math.min(share, 1 - share);
  const minCounterShare = VOLUME_DOT_MIN_VISIBLE_WEDGE_DEG / 360;
  return {
    side,
    bull,
    bear,
    total,
    share,
    tone: share >= 0.5 ? 'bullish' : 'bearish',
    rawCounterShare,
    counterShare: rawCounterShare === 0 ? 0 : Math.max(rawCounterShare, minCounterShare),
  };
}

function cssDeg(value) {
  return Number(value.toFixed(3));
}

export function selectVolumeDots(flows = [], minVolume = VOLUME_DOT_MIN_VOLUME) {
  if (minVolume === null) return [];
  const positiveFlows = flows
    .filter((flow) => num(flow.dVol) >= minVolume)
    .sort((a, b) => a.t - b.t);
  const allVols = positiveFlows.map((flow) => flow.dVol);
  const p98 = percentile(allVols, 0.98) || 1;
  const median = percentile(allVols, 0.5) || 1;

  return positiveFlows.map((flow) => {
    const buy = num(flow.dBuy ?? flow.buy);
    const sell = num(flow.dSell ?? flow.sell);
    const total = buy + sell;
    const rawBoughtDelta = num(flow.dDeltaBuy ?? flow.boughtDelta);
    const rawSoldDelta = num(flow.dDeltaSell ?? flow.soldDelta);
    const hasDeltaSides = Math.abs(rawBoughtDelta) + Math.abs(rawSoldDelta) > 0;
    const boughtDelta = hasDeltaSides ? rawBoughtDelta : 0;
    const soldDelta = hasDeltaSides ? rawSoldDelta : 0;
    // Feed convention: net = bought - sold (selling negative-delta puts is bullish). Only
    // used when the feed omits its own net counter; must match that convention, not the sum.
    const netDelta = num(flow.dDeltaNet ?? flow.netDelta ?? boughtDelta - soldDelta);
    const deltaEstimated = !hasDeltaSides && netDelta !== 0;
    const buyBull = num(flow.dDeltaBuyBull ?? flow.boughtDeltaBull);
    const buyBear = num(flow.dDeltaBuyBear ?? flow.boughtDeltaBear);
    const sellBull = num(flow.dDeltaSellBull ?? flow.soldDeltaBull);
    const sellBear = num(flow.dDeltaSellBear ?? flow.soldDeltaBear);
    const directionalBullish = buyBull + sellBull;
    const directionalBearish = buyBear + sellBear;
    const hasDirectionalSplit = directionalBullish + directionalBearish > 0;
    const boughtContribution = boughtDelta;
    const soldContribution = -soldDelta;
    const fallbackBullish = Math.max(0, boughtContribution) + Math.max(0, soldContribution);
    const fallbackBearish = Math.max(0, -boughtContribution) + Math.max(0, -soldContribution);
    const bullishDelta = hasDirectionalSplit ? directionalBullish : fallbackBullish;
    const bearishDelta = hasDirectionalSplit ? directionalBearish : fallbackBearish;
    const grossDelta = bullishDelta + bearishDelta;
    const directionalSegments = [directionalSegment('aggregate', bullishDelta, bearishDelta)].filter(Boolean);
    const volumeShare = total > 0 ? buy / total : 0.5;
    // Green share = bullish directional contribution, red share = bearish
    // directional contribution. When the per-contract decomposition survives
    // upstream aggregation, use it; otherwise fall back to the older sign-folded
    // split from aggregate buy/sell deltas.
    const deltaShare = grossDelta > 0
      ? bullishDelta / grossDelta
      : (deltaEstimated ? (netDelta >= 0 ? 1 : 0) : volumeShare);
    return {
      ...flow,
      buy,
      sell,
      boughtDelta,
      soldDelta,
      netDelta,
      bullishDelta,
      bearishDelta,
      grossDelta,
      directionalSegments,
      deltaEstimated,
      volumeShare,
      deltaShare,
      share: deltaEstimated ? deltaShare : displayVolumeDotShare(deltaShare),
      r: flow.recoveryGap ? VOLUME_DOT_RECOVERY_RADIUS : dotRadius(flow.dVol, p98),
      climax: isClimaxPrint(flow.dVol, median),
      recoveryGap: Boolean(flow.recoveryGap),
      gapMinutes: Number(flow.gapMinutes) || 0,
    };
  });
}

function dotToneRgb(tone) {
  return tone === 'bullish' ? '87,255,104' : '255,96,75';
}

export function volumeDotSegmentGeometry(segments = []) {
  const total = segments.reduce((sum, seg) => sum + num(seg.total), 0);
  if (total <= 0) return [];

  let cursor = -90;
  const out = [];
  for (const seg of segments) {
    const segDeg = (num(seg.total) / total) * 360;
    const counterShare = Math.max(0, Math.min(0.5, num(seg.counterShare)));
    const counterDeg = counterShare * segDeg;
    let counterStart = null;
    if (counterDeg > 0) {
      // Canvas angles start at 3 o'clock, so top/bottom anchors are -90/90 here.
      const counterCenter = seg.tone === 'bullish' ? -90 : 90;
      counterStart = counterCenter - counterDeg / 2;
    }
    out.push({
      side: seg.side,
      tone: seg.tone,
      startDeg: cssDeg(cursor),
      sweepDeg: cssDeg(segDeg),
      counterStartDeg: counterStart == null ? null : cssDeg(counterStart),
      counterSweepDeg: cssDeg(counterDeg),
    });
    cursor += segDeg;
  }
  return out;
}

export function volumeDotGradient(dotOrShare, estimated = false, netDelta = 0) {
  const dot = dotOrShare && typeof dotOrShare === 'object' ? dotOrShare : null;
  const share = dot ? dot.share : dotOrShare;
  const isEstimated = dot ? dot.deltaEstimated : estimated;
  const net = dot ? dot.netDelta : netDelta;
  if (isEstimated) {
    const fill = volumeDotNetDeltaMutedRgb(net);
    const style = VOLUME_DOT_AMBIGUOUS_STYLE;
    return `repeating-linear-gradient(${style.hatchAngleDeg}deg, rgba(${style.hatchColor},${style.hatchAlpha}) 0 ${style.hatchWidthPx}px, transparent ${style.hatchWidthPx}px ${style.hatchGapPx}px), radial-gradient(circle, rgb(${fill}), rgb(${fill}))`;
  }
  const greenShare = Math.max(0, Math.min(1, share));
  const bullishDominant = greenShare >= 0.5;
  const baseColor = dotToneRgb(bullishDominant ? 'bullish' : 'bearish');
  const base = `radial-gradient(circle, rgba(${baseColor},0.98), rgba(${baseColor},0.9))`;
  const minorityShare = Math.min(greenShare, 1 - greenShare);
  if (minorityShare === 0) return base;
  const counterDeg = Math.round(minorityShare * 360);
  // CSS conic-gradient angles start at 12 o'clock, so top/bottom anchors are 0/180 here.
  const counterCenter = bullishDominant ? 0 : 180;
  const counterStart = counterCenter - counterDeg / 2;
  return `conic-gradient(from ${counterStart}deg, rgba(3,7,18,0.58) 0deg ${counterDeg}deg, transparent ${counterDeg}deg 360deg), ${base}`;
}

export function volumeDotNetDeltaRgb(netDelta) {
  const n = Number(netDelta);
  if (!Number.isFinite(n) || n === 0) return '148,163,184';
  return n > 0 ? '87,255,104' : '255,96,75';
}

export function volumeDotNetDeltaMutedRgb(netDelta) {
  const n = Number(netDelta);
  if (!Number.isFinite(n) || n === 0) return '75,82,94';
  return n > 0 ? '38,89,44' : '104,48,39';
}

export function volumeDotAmbiguousCanvasStyle(netDelta) {
  const style = VOLUME_DOT_AMBIGUOUS_STYLE;
  return {
    fillStyle: `rgb(${volumeDotNetDeltaMutedRgb(netDelta)})`,
    hatchStyle: `rgba(${style.hatchColor},${style.hatchAlpha})`,
    hatchLineWidth: style.canvasHatchWidthPx,
    hatchGap: style.hatchGapPx,
  };
}

// A climax print exceeds 3x the rolling median gross volume.
export function isClimaxPrint(dVol, medianVol) {
  return dVol > Math.max(1, medianVol) * 3;
}
